import {
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";


import {
    io,
} from "socket.io-client";


const BACKEND_URL =
    import.meta.env.VITE_BACKEND_URL ??
    "";


const DASHBOARD_NAMESPACE =
    "/dashboard";


const RADAR_PAIRS = [
    "BTCUSDT",
    "ETHUSDT",
    "SOLUSDT",
];


const INITIAL_HEALTH = {
    heartbeat:
        "DISCONNECTED",

    redis:
        "UNKNOWN",

    tradingRuntime:
        null,

    marketData:
        null,

    telegram:
        null,

    dashboard: {
        running:
            false,

        clients:
            0,
    },

    error:
        null,

    timestamp:
        null,
};


const buildInitialRadar = () =>
    Object.fromEntries(
        RADAR_PAIRS.map(
            (
                pair
            ) => [
                pair,
                {
                    pair,

                    status:
                        "NO SETUP",

                    completed_steps:
                        [],

                    missing_confluence:
                        "Waiting for market structure to develop",

                    distance_to_trigger:
                        null,

                    timestamp:
                        null,
                },
            ]
        )
    );


const buildDashboardUrl = () => {
    const baseUrl =
        String(
            BACKEND_URL
        )
            .trim()
            .replace(
                /\/+$/,
                ""
            );


    return baseUrl
        ? baseUrl +
              DASHBOARD_NAMESPACE
        : DASHBOARD_NAMESPACE;
};


const extractCandle = (
    payload
) => {
    if (
        !payload
    ) {
        return null;
    }


    const candidates = [
        payload?.payload?.candle,
        payload?.candle,
        payload?.payload,
        payload,
    ];


    for (
        const candidate of
        candidates
    ) {
        if (
            candidate &&
            typeof candidate ===
                "object" &&
            (
                candidate.open !==
                    undefined ||
                candidate.high !==
                    undefined ||
                candidate.low !==
                    undefined ||
                candidate.close !==
                    undefined
            )
        ) {
            return candidate;
        }
    }


    return null;
};


const extractRadar = (
    payload
) => {
    const source =
        payload?.payload &&
        typeof payload.payload ===
            "object"
            ? payload.payload
            : payload;


    const pair =
        String(
            source?.pair ??
                source?.symbol ??
                payload?.symbol ??
                ""
        )
            .trim()
            .toUpperCase();


    if (
        !pair
    ) {
        return null;
    }


    return {
        ...source,

        pair,

        status:
            source?.status ??
            "DEVELOPING",

        completed_steps:
            source?.completed_steps ??
            source?.completedSteps ??
            [],

        missing_confluence:
            source?.missing_confluence ??
            source?.missingConfluence ??
            null,

        distance_to_trigger:
            source?.distance_to_trigger ??
            source?.distanceToTrigger ??
            null,

        timestamp:
            payload?.timestamp ??
            source?.timestamp ??
            new Date().toISOString(),
    };
};


export function useDashboardSocket() {
    const socketRef =
        useRef(null);


    const [
        connected,
        setConnected,
    ] = useState(false);


    const [
        health,
        setHealth,
    ] = useState(
        INITIAL_HEALTH
    );


    const [
        candle,
        setCandle,
    ] = useState(null);


    const [
        candidate,
        setCandidate,
    ] = useState(null);


    const [
        gemini,
        setGemini,
    ] = useState(null);


    const [
        order,
        setOrder,
    ] = useState(null);


    const [
        position,
        setPosition,
    ] = useState(null);


    const [
        events,
        setEvents,
    ] = useState([]);


    const [
        radarByPair,
        setRadarByPair,
    ] = useState(
        buildInitialRadar
    );


    const [
        geminiMessages,
        setGeminiMessages,
    ] = useState([]);


    useEffect(() => {
        const dashboardUrl =
            buildDashboardUrl();


        console.info(
            "[DashboardSocket] Connecting to:",
            dashboardUrl
        );


        const socket =
            io(
                dashboardUrl,
                {
                    path:
                        "/socket.io",

                    transports: [
                        "websocket",
                        "polling",
                    ],

                    autoConnect:
                        true,

                    reconnection:
                        true,
                }
            );


        socketRef.current =
            socket;


        const pushEvent =
            (
                type,
                payload
            ) => {
                setEvents(
                    (
                        current
                    ) => [
                        {
                            id:
                                `${Date.now()}-${Math.random()}`,

                            type,

                            timestamp:
                                payload?.timestamp ??
                                new Date()
                                    .toISOString(),

                            payload,
                        },

                        ...current,
                    ].slice(
                        0,
                        100
                    )
                );
            };


        socket.on(
            "connect",
            () => {
                console.info(
                    "[DashboardSocket] Connected:",
                    socket.id
                );


                setConnected(
                    true
                );


                socket.emit(
                    "dashboard_health"
                );
            }
        );


        socket.on(
            "disconnect",
            (
                reason
            ) => {
                console.warn(
                    "[DashboardSocket] Disconnected:",
                    reason
                );


                setConnected(
                    false
                );
            }
        );


        socket.on(
            "connect_error",
            (
                error
            ) => {
                console.error(
                    "[DashboardSocket] Connection error:",
                    error
                );


                setConnected(
                    false
                );
            }
        );


        socket.on(
            "system_health",
            (
                payload
            ) => {
                setHealth(
                    payload ??
                    INITIAL_HEALTH
                );


                pushEvent(
                    "system_health",
                    payload
                );
            }
        );


        socket.on(
            "radar_snapshot",
            (
                payload
            ) => {
                const pairs =
                    payload?.pairs ??
                    {};

                setRadarByPair(
                    (
                        current
                    ) => {
                        const next = {
                            ...current,
                        };

                        for (
                            const pair of
                            RADAR_PAIRS
                        ) {
                            if (
                                pairs?.[
                                    pair
                                ]
                            ) {
                                next[
                                    pair
                                ] =
                                    pairs[
                                        pair
                                    ];
                            }
                        }

                        return next;
                    }
                );

                pushEvent(
                    "radar_snapshot",
                    payload
                );
            }
        );


        socket.on(
            "radar_update",
            (
                payload
            ) => {
                const radar =
                    extractRadar(
                        payload
                    );

                if (
                    radar &&
                    RADAR_PAIRS.includes(
                        radar.pair
                    )
                ) {
                    setRadarByPair(
                        (
                            current
                        ) => ({
                            ...current,

                            [radar.pair]:
                                {
                                    ...current[
                                        radar.pair
                                    ],

                                    ...radar,
                                },
                        })
                    );
                }

                pushEvent(
                    "radar_update",
                    payload
                );
            }
        );


        socket.on(
            "candle_update",
            (
                payload
            ) => {
                const receivedCandle =
                    extractCandle(
                        payload
                    );


                if (
                    receivedCandle
                ) {
                    setCandle(
                        payload
                    );
                }


                pushEvent(
                    "candle_update",
                    payload
                );
            }
        );


        socket.on(
            "candidate_detected",
            (
                payload
            ) => {
                setCandidate(
                    payload
                );


                pushEvent(
                    "candidate_detected",
                    payload
                );
            }
        );


        socket.on(
            "market_radar_update",
            (
                payload
            ) => {
                const radar =
                    extractRadar(
                        payload
                    );


                if (
                    radar
                ) {
                    setRadarByPair(
                        (
                            current
                        ) => ({
                            ...current,

                            [radar.pair]:
                                radar,
                        })
                    );
                }


                pushEvent(
                    "market_radar_update",
                    payload
                );
            }
        );


        socket.on(
            "gemini_analyzed",
            (
                payload
            ) => {
                setGemini(
                    payload
                );


                pushEvent(
                    "gemini_analyzed",
                    payload
                );
            }
        );


        socket.on(
            "gemini_chat_response",
            (
                payload
            ) => {
                const status =
                    String(
                        payload?.status ??
                            "COMPLETED"
                    ).toUpperCase();


                if (
                    status ===
                    "STARTED"
                ) {
                    setGeminiMessages(
                        (
                            current
                        ) => [
                            ...current,

                            {
                                id:
                                    `${payload?.requestId ?? Date.now()}-started`,

                                requestId:
                                    payload?.requestId ??
                                    null,

                                role:
                                    "assistant",

                                status:
                                    "STARTED",

                                timestamp:
                                    payload?.timestamp ??
                                    new Date()
                                        .toISOString(),
                            },
                        ]
                    );
                } else {
                    setGeminiMessages(
                        (
                            current
                        ) => [
                            ...current,

                            {
                                id:
                                    `${payload?.requestId ?? Date.now()}-${Date.now()}`,

                                requestId:
                                    payload?.requestId ??
                                    null,

                                role:
                                    "assistant",

                                status,

                                content:
                                    payload?.answer ??
                                    null,

                                error:
                                    payload?.error ??
                                    null,

                                marketContext:
                                    payload?.market_context ??
                                    null,

                                authorityStatement:
                                    payload?.authority_statement ??
                                    null,

                                timestamp:
                                    payload?.timestamp ??
                                    new Date()
                                        .toISOString(),
                            },
                        ]
                    );
                }


                pushEvent(
                    "gemini_chat_response",
                    payload
                );
            }
        );


        socket.on(
            "order_placed",
            (
                payload
            ) => {
                setOrder(
                    payload
                );


                pushEvent(
                    "order_placed",
                    payload
                );
            }
        );


        socket.on(
            "position_updated",
            (
                payload
            ) => {
                setPosition(
                    payload
                );


                pushEvent(
                    "position_updated",
                    payload
                );
            }
        );


        return () => {
            socket.removeAllListeners();
            socket.disconnect();

            socketRef.current =
                null;
        };
    }, []);


    const sendGeminiQuery =
        useCallback(
            (
                question
            ) => {
                const normalized =
                    String(
                        question ??
                            ""
                    ).trim();


                if (
                    !normalized
                ) {
                    return null;
                }


                if (
                    !socketRef.current
                        ?.connected
                ) {
                    throw new Error(
                        "Dashboard Socket.IO connection is not available."
                    );
                }


                const requestId =
                    `${Date.now()}-${Math.random()
                        .toString(36)
                        .slice(2, 10)}`;


                setGeminiMessages(
                    (
                        current
                    ) => [
                        ...current,

                        {
                            id:
                                `${requestId}-user`,

                            requestId,

                            role:
                                "user",

                            status:
                                "SENT",

                            content:
                                normalized,

                            timestamp:
                                new Date()
                                    .toISOString(),
                        },
                    ]
                );


                socketRef.current.emit(
                    "user_query_gemini",
                    {
                        requestId,

                        question:
                            normalized,
                    }
                );


                return requestId;
            },
            []
        );


    return {
        connected,

        health,

        candle,

        candidate,

        gemini,

        order,

        position,

        events,

        radarByPair,

        geminiMessages,

        sendGeminiQuery,
    };
}


export default useDashboardSocket;